import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Tooltip,
  TextField,
} from '@mui/material'
import { Trash2, Check, RotateCcw } from 'react-feather'
import { formatDistanceToNow } from 'date-fns'
import { AttentionTask } from '@/types'
import { useDeleteAttentionTask } from '@/hooks/api/useDeleteAttentionTask'
import { usePatchAttentionTask } from '@/hooks/api/usePatchAttentionTask'
import { useMutationWrapper } from '@/hooks'

type AttentionTaskListProps = {
  tasks: AttentionTask[]
  targetType: AttentionTask['target_type']
  targetId: number
}

export const AttentionTaskList = ({
  tasks,
  targetType,
  targetId,
}: AttentionTaskListProps): JSX.Element => {
  const { t } = useTranslation(['monitoring', 'common'])
  const [taskToDelete, setTaskToDelete] = useState<number | null>(null)

  const { mutateAsync: deleteTask, isLoading: isDeleting } = useMutationWrapper(
    () =>
      useDeleteAttentionTask({
        target_type: targetType,
        target_id: targetId,
      })
  )

  const { mutateAsync: patchTask, isLoading: isPatching } = useMutationWrapper(
    () =>
      usePatchAttentionTask({
        target_type: targetType,
        target_id: targetId,
      })
  )

  const [reworkTaskId, setReworkTaskId] = useState<number | null>(null)
  const [reworkMessage, setReworkMessage] = useState<string>('')

  const handleDeleteClick = (taskId: number) => {
    setTaskToDelete(taskId)
  }

  const handleConfirmDelete = async () => {
    if (taskToDelete !== null) {
      await deleteTask(taskToDelete, {
        successMessageKey: 'monitoring:notifications.attentionTaskDeleted',
      })
      setTaskToDelete(null)
    }
  }

  const handleCancelDelete = () => {
    setTaskToDelete(null)
  }

  const handleCancelRework = () => {
    setReworkTaskId(null)
    setReworkMessage('')
  }

  const handleConfirmRework = async () => {
    if (reworkTaskId !== null) {
      await patchTask(
        { id: reworkTaskId, status: 'rework', rework_message: reworkMessage },
        { successMessageKey: 'monitoring:notifications.attentionTaskReworked' }
      )
      handleCancelRework()
    }
  }

  if (tasks.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t('monitoring:noAttentionTasks')}
      </Typography>
    )
  }

  return (
    <>
      <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
        {tasks.map((task) => (
          <ListItem
            key={task.id}
            alignItems="flex-start"
            secondaryAction={
              <Box display="flex" alignItems="center" gap={0.5}>
                {task.status === 'completed' && (
                  <>
                    <Tooltip
                      title={t('monitoring:buttons.acceptAttentionTask')}
                    >
                      <span>
                        <IconButton
                          edge="end"
                          size="small"
                          color="success"
                          aria-label={t(
                            'monitoring:buttons.acceptAttentionTask'
                          )}
                          onClick={() =>
                            patchTask(
                              { id: task.id, status: 'accepted' },
                              {
                                successMessageKey:
                                  'monitoring:notifications.attentionTaskAccepted',
                              }
                            )
                          }
                          disabled={isPatching}
                        >
                          <Check size={18} />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip
                      title={t('monitoring:buttons.reworkAttentionTask')}
                    >
                      <span>
                        <IconButton
                          edge="end"
                          size="small"
                          color="warning"
                          aria-label={t(
                            'monitoring:buttons.reworkAttentionTask'
                          )}
                          onClick={() => {
                            setReworkTaskId(task.id)
                            setReworkMessage(task.rework_message || '')
                          }}
                          disabled={isPatching}
                        >
                          <RotateCcw size={18} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </>
                )}
                <Tooltip title={t('common:delete')}>
                  <IconButton
                    edge="end"
                    aria-label={t('common:delete')}
                    onClick={() => handleDeleteClick(task.id)}
                  >
                    <Trash2 size={18} />
                  </IconButton>
                </Tooltip>
              </Box>
            }
            sx={{
              borderBottom: '1px solid',
              borderColor: 'divider',
              py: 2,
            }}
          >
            <ListItemText
              primary={
                <Box display="flex" alignItems="center" mb={1}>
                  <Typography
                    variant="body1"
                    component="span"
                    fontWeight="medium"
                  >
                    {task.message}
                  </Typography>
                </Box>
              }
              secondary={
                <Box>
                  <Box display="flex" alignItems="center" mt={1}>
                    <Typography variant="caption" color="text.secondary" mr={1}>
                      {t('monitoring:createdBy')}: {task.created_by.first_name}{' '}
                      {task.created_by.last_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatDistanceToNow(new Date(task.created_at), {
                        addSuffix: true,
                      })}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" mt={1}>
                    <Chip
                      label={
                        task.status === 'completed'
                          ? t('monitoring:completed')
                          : task.status === 'accepted'
                          ? t('monitoring:accepted')
                          : task.status === 'rework'
                          ? t('monitoring:rework')
                          : t('monitoring:todo')
                      }
                      size="small"
                      color={
                        task.status === 'completed'
                          ? 'success'
                          : task.status === 'accepted'
                          ? 'success'
                          : task.status === 'rework'
                          ? 'error'
                          : 'warning'
                      }
                      sx={{ height: 24 }}
                    />
                    {task.status === 'completed' && task.completed_by && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        ml={1}
                      >
                        {t('monitoring:completedBy')}:{' '}
                        {task.completed_by.first_name}{' '}
                        {task.completed_by.last_name}
                      </Typography>
                    )}
                  </Box>
                  {task.status === 'rework' && task.rework_message && (
                    <Box mt={1}>
                      <Typography variant="caption" color="text.secondary">
                        {t('monitoring:rework')}: {task.rework_message}
                      </Typography>
                    </Box>
                  )}
                </Box>
              }
            />
          </ListItem>
        ))}
      </List>

      <Dialog open={taskToDelete !== null} onClose={handleCancelDelete}>
        <DialogTitle>
          {t('monitoring:titles.deleteAttentionTaskTitle')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('monitoring:deleteAttentionTaskConfirmation')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary">
            {t('common:cancel')}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={isDeleting}
          >
            {t('common:delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={reworkTaskId !== null}
        onClose={handleCancelRework}
        fullWidth={true}
      >
        <DialogTitle>
          {t('monitoring:titles.reworkAttentionTaskTitle')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t('monitoring:reworkAttentionTaskDescription')}
          </DialogContentText>
          <TextField
            autoFocus={true}
            fullWidth={true}
            minRows={3}
            multiline={true}
            label={t('monitoring:reworkMessageLabel')}
            placeholder={t('monitoring:placeholders.reworkMessage')}
            value={reworkMessage}
            onChange={(e) => setReworkMessage(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelRework} color="primary">
            {t('common:cancel')}
          </Button>
          <Button
            onClick={handleConfirmRework}
            color="warning"
            variant="contained"
            disabled={isPatching || !reworkMessage.trim()}
          >
            {t('monitoring:buttons.confirmReworkAttentionTask')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
